'use client';

import { ArrowLeft, ArrowRight, X } from 'lucide-react';
import Image from 'next/image';
import { usePathname } from 'next/navigation';
import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useLocationPreference } from '@/app/(users)/totem-by-slug/[slug]/hooks/use-location-preference';
import { useOptionalTotem } from '@/app/(users)/totem-by-slug/[slug]/providers/totem-provider';
import { Button } from '@/components/ui/button';
import type { LocationPreferences } from '@/interface/location-preferences';
import { cn } from '@/lib/utils';

export interface TotemOrderOptionsOverlayProps {
  orderOptionsOpen?: boolean;
  setOrderOptionsOpen?: (open: boolean) => void;
  orderType?: 'takeaway' | 'eat_here' | null;
  setOrderType?: (type: 'takeaway' | 'eat_here' | null) => void;
  pickupMethod?: 'banderin' | 'beeper' | 'nombre' | null;
  setPickupMethod?: (method: 'banderin' | 'beeper' | 'nombre' | null) => void;
  beeperNumber?: string;
  setBeeperNumber?: (num: string) => void;
  locationTitle?: string;
  locationPreference?: LocationPreferences | null;
}

export function TotemOrderOptionsOverlay(props: TotemOrderOptionsOverlayProps = {}) {
  const totemCtx = useOptionalTotem();
  const { data: queryLocationPreference } = useLocationPreference(
    props.locationPreference ? undefined : (totemCtx?.totem?.location_id ?? undefined)
  );

  const orderOptionsOpen = props.orderOptionsOpen ?? totemCtx?.orderOptionsOpen ?? false;
  const setOrderOptionsOpen = props.setOrderOptionsOpen ?? totemCtx?.setOrderOptionsOpen ?? (() => {});
  const orderType = props.orderType !== undefined ? props.orderType : (totemCtx?.orderType ?? null);
  const setOrderType = props.setOrderType ?? totemCtx?.setOrderType ?? (() => {});
  const pickupMethod = props.pickupMethod !== undefined ? props.pickupMethod : (totemCtx?.pickupMethod ?? null);
  const setPickupMethod = props.setPickupMethod ?? totemCtx?.setPickupMethod ?? (() => {});
  const beeperNumber = props.beeperNumber !== undefined ? props.beeperNumber : (totemCtx?.beeperNumber ?? '');
  const setBeeperNumber = props.setBeeperNumber ?? totemCtx?.setBeeperNumber ?? (() => {});
  const locationTitle = props.locationTitle ?? totemCtx?.totem?.locations?.title ?? '';
  const locationPreference = props.locationPreference ?? queryLocationPreference;

  const pathname = usePathname();
  const isDashboard = pathname?.includes('/takeorder');

  const [step, setStep] = useState<1 | 2>(1);
  const inputRef = useRef<HTMLInputElement>(null);

  const hasTakeaway = Boolean(locationPreference?.take_away);
  const hasEatHere = Boolean(locationPreference?.eat_here);
  const hasFlag = Boolean(locationPreference?.flag);
  const hasBeeper = Boolean(locationPreference?.beeper);
  const hasName = Boolean(locationPreference?.name);
  const eatHereMethodCount = (hasFlag ? 1 : 0) + (hasBeeper ? 1 : 0) + (hasName ? 1 : 0);

  const singleMethod = useMemo<'banderin' | 'beeper' | 'nombre' | null>(() => {
    if (eatHereMethodCount !== 1) return null;
    if (hasFlag) return 'banderin';
    if (hasBeeper) return 'beeper';
    if (hasName) return 'nombre';
    return null;
  }, [eatHereMethodCount, hasFlag, hasBeeper, hasName]);

  useEffect(() => {
    if (pickupMethod !== null) {
      const timeout = setTimeout(() => {
        inputRef.current?.focus();
      }, 50);
      return () => clearTimeout(timeout);
    }
  }, [pickupMethod]);

  // Dynamic step initialization based on hybrid flow
  useEffect(() => {
    if (orderOptionsOpen) {
      if (orderType === 'eat_here' || (!hasTakeaway && hasEatHere)) {
        setStep(2);
        if (singleMethod && !pickupMethod) {
          setPickupMethod(singleMethod);
        }
      } else {
        setStep(1);
      }
    }
  }, [orderOptionsOpen, orderType, hasTakeaway, hasEatHere, singleMethod, pickupMethod, setPickupMethod]);

  if (!orderOptionsOpen) return null;

  const canDismiss =
    orderType !== null &&
    (orderType === 'takeaway' || eatHereMethodCount === 0 || (pickupMethod !== null && beeperNumber.trim() !== ''));

  const handleTakeaway = () => {
    setOrderType('takeaway');
    setPickupMethod(null);
    setBeeperNumber('');
    setOrderOptionsOpen(false);
  };

  const handleDineIn = () => {
    setOrderType('eat_here');
    if (eatHereMethodCount === 0) {
      setPickupMethod(null);
      setBeeperNumber('');
      setOrderOptionsOpen(false);
      return;
    }
    if (singleMethod) {
      setPickupMethod(singleMethod);
    }
    setStep(2);
  };

  const handleContinue = () => {
    setOrderOptionsOpen(false);
  };

  const handleBack = () => {
    if (pickupMethod !== null && eatHereMethodCount > 1) {
      setPickupMethod(null);
      setBeeperNumber('');
    } else if (step === 2 && hasTakeaway && hasEatHere) {
      setStep(1);
    } else if (canDismiss) {
      setOrderOptionsOpen(false);
    }
  };

  let wrapperScaleClass = '';
  if (pickupMethod !== null) {
    wrapperScaleClass = 'scale-[0.70] origin-top transition-transform duration-300';
  } else {
    wrapperScaleClass = 'scale-[0.75] origin-top transition-transform duration-300';
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      className={cn(
        'inset-0 z-[100] bg-black/75 backdrop-blur-sm flex flex-col pt-10 px-6 overflow-hidden',
        isDashboard ? 'absolute' : 'fixed'
      )}
    >
      <div className={cn('flex flex-col flex-1 w-full transition-transform', wrapperScaleClass)}>
        {/* Back Button */}
        {step === 2 && (hasTakeaway || (eatHereMethodCount > 1 && pickupMethod !== null)) && (
          <Button
            variant="ghost"
            className="absolute top-10 left-6 text-white hover:text-white hover:bg-white/10 rounded-full pl-3 pr-5 py-6 font-semibold text-lg"
            onClick={handleBack}
          >
            <div className="bg-white text-black rounded-full p-1.5 mr-3">
              <ArrowLeft className="size-5" />
            </div>
            Volver
          </Button>
        )}

        {/* Close Button (if re-opened with already selected options) */}
        {canDismiss && (
          <button
            type="button"
            onClick={() => setOrderOptionsOpen(false)}
            aria-label="Cerrar"
            className="absolute top-10 right-6 text-white hover:text-white hover:bg-white/10 rounded-full p-3 transition-colors"
          >
            <X className="size-8" />
          </button>
        )}

        {/* Header Logo/Title */}
        <div className="flex flex-col items-center gap-2 mb-4 xl:mb-10 text-white mt-0 xl:mt-4">
          <div className="flex items-center gap-3">
            <h2 className="text-3xl font-bold">{locationTitle}</h2>
          </div>
          {step === 2 && (
            <p className="text-xl xl:text-2xl mt-4 xl:mt-8 mb-2 xl:mb-4">
              Seleccioná una opción para retirar tu pedido
            </p>
          )}
        </div>

        <div className="flex-1 flex flex-col items-center justify-center pb-10 xl:pb-20">
          {step === 1 ? (
            <div className="flex flex-col gap-4 sm:gap-6 w-full max-w-lg px-4 sm:px-0">
              {hasTakeaway && (
                <button
                  type="button"
                  onClick={handleTakeaway}
                  className="flex items-center justify-center gap-4 sm:gap-6 lg:gap-8 bg-white hover:bg-slate-50 text-black p-6 sm:p-8 lg:p-10 rounded-3xl transition-transform active:scale-[0.98] shadow-2xl group"
                >
                  <div className="relative w-12 h-12 sm:w-16 sm:h-16 lg:w-20 lg:h-20 shrink-0">
                    <Image src="/icons/take-away.png" alt="Para llevar" fill className="object-contain" />
                  </div>
                  <span className="text-xl sm:text-2xl lg:text-3xl font-bold whitespace-nowrap">Para llevar</span>
                </button>
              )}

              {hasEatHere && (
                <button
                  type="button"
                  onClick={handleDineIn}
                  className="flex items-center justify-center gap-4 sm:gap-6 lg:gap-8 bg-white hover:bg-slate-50 text-black p-6 sm:p-8 lg:p-10 rounded-3xl transition-transform active:scale-[0.98] shadow-2xl group"
                >
                  <div className="relative w-12 h-12 sm:w-16 sm:h-16 lg:w-20 lg:h-20 shrink-0">
                    <Image
                      src="/icons/eat-here.png"
                      alt="Para comer aquí"
                      fill
                      className="brightness-0 object-contain"
                    />
                  </div>
                  <span className="text-xl sm:text-2xl lg:text-3xl font-bold whitespace-nowrap">Para comer aquí</span>
                </button>
              )}
            </div>
          ) : (
            <div className="flex flex-col items-center gap-6 xl:gap-12 w-full max-w-3xl">
              {eatHereMethodCount > 1 && (
                <div className="flex gap-4 xl:gap-6 justify-center w-full">
                  {hasFlag && (
                    <button
                      type="button"
                      onClick={() => {
                        if (pickupMethod !== 'banderin') setBeeperNumber('');
                        setPickupMethod('banderin');
                      }}
                      className={cn(
                        'flex-1 flex flex-col items-center gap-3 xl:gap-6 bg-white p-4 xl:p-10 rounded-3xl transition-all border-4 shadow-xl active:scale-[0.98]',
                        pickupMethod === 'banderin' ? 'border-emerald-500 scale-105' : 'border-transparent opacity-90'
                      )}
                    >
                      <Image
                        src="/icons/banderin-modal.png"
                        alt="Banderín"
                        width={96}
                        height={96}
                        className="w-16 h-16 xl:w-24 xl:h-24 object-contain"
                      />
                      <span className="text-xl xl:text-2xl font-semibold text-zinc-800">Banderín</span>
                    </button>
                  )}

                  {hasBeeper && (
                    <button
                      type="button"
                      onClick={() => {
                        if (pickupMethod !== 'beeper') setBeeperNumber('');
                        setPickupMethod('beeper');
                      }}
                      className={cn(
                        'flex-1 flex flex-col items-center gap-3 xl:gap-6 bg-white p-4 xl:p-10 rounded-3xl transition-all border-4 shadow-xl active:scale-[0.98]',
                        pickupMethod === 'beeper' ? 'border-emerald-500 scale-105' : 'border-transparent opacity-90'
                      )}
                    >
                      <Image
                        src="/icons/beeper.png"
                        alt="Beeper"
                        width={96}
                        height={96}
                        className="w-16 h-16 xl:w-24 xl:h-24 object-contain"
                      />
                      <span className="text-xl xl:text-2xl font-semibold text-zinc-800">Beeper</span>
                    </button>
                  )}

                  {hasName && (
                    <button
                      type="button"
                      onClick={() => {
                        if (pickupMethod !== 'nombre') setBeeperNumber('');
                        setPickupMethod('nombre');
                      }}
                      className={cn(
                        'flex-1 flex flex-col items-center gap-3 xl:gap-6 bg-white p-4 xl:p-10 rounded-3xl transition-all border-4 shadow-xl active:scale-[0.98]',
                        pickupMethod === 'nombre' ? 'border-emerald-500 scale-105' : 'border-transparent opacity-90'
                      )}
                    >
                      <Image
                        src="/icons/nombre.png"
                        alt="Nombre"
                        width={96}
                        height={96}
                        className="w-16 h-16 xl:w-24 xl:h-24 object-contain"
                      />
                      <span className="text-xl xl:text-2xl font-semibold text-zinc-800">Nombre</span>
                    </button>
                  )}
                </div>
              )}

              {/* Identifier Input and Continue Button */}
              {pickupMethod !== null && (
                <div className="bg-white w-full max-w-2xl rounded-3xl p-3 sm:p-4 xl:p-8 shadow-2xl animate-in slide-in-from-top-4 fade-in duration-300">
                  <label
                    htmlFor="identifier-input"
                    className="block text-base sm:text-lg xl:text-2xl font-semibold mb-2 xl:mb-4 text-zinc-800"
                  >
                    {pickupMethod === 'banderin' && 'Ingresá el número de banderín'}
                    {pickupMethod === 'beeper' && 'Ingresá el número de beeper'}
                    {pickupMethod === 'nombre' && 'Ingresá tu nombre'}
                  </label>
                  <div className="flex items-center gap-2 sm:gap-4">
                    <div className="relative flex-1">
                      <input
                        ref={inputRef}
                        id="identifier-input"
                        type={pickupMethod === 'nombre' ? 'text' : 'number'}
                        value={beeperNumber}
                        maxLength={pickupMethod === 'nombre' ? 20 : 3}
                        onChange={e => {
                          const val = e.target.value;
                          if (pickupMethod !== 'nombre') {
                            if (val.length <= 3) setBeeperNumber(val);
                          } else {
                            if (val.length <= 20) setBeeperNumber(val);
                          }
                        }}
                        onKeyDown={e => {
                          if (e.key === 'Enter' && beeperNumber.trim()) {
                            handleContinue();
                          }
                        }}
                        placeholder={
                          pickupMethod === 'beeper'
                            ? 'Ingresa el número'
                            : pickupMethod === 'banderin'
                              ? 'Ingresa el número'
                              : 'Tu nombre'
                        }
                        className="w-full bg-slate-100 dark:bg-slate-100 rounded-2xl h-12 sm:h-14 xl:h-24 text-center px-10 sm:px-16 xl:px-24 text-lg sm:text-2xl xl:text-4xl font-bold text-zinc-800 dark:text-zinc-800 outline-none focus:ring-4 focus:ring-emerald-500/20 transition-all placeholder:text-slate-400 dark:placeholder:text-slate-400 placeholder:text-sm sm:placeholder:text-base xl:placeholder:text-2xl placeholder:font-medium [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                      />
                      <span className="absolute top-1/2 -translate-y-1/2 right-3 sm:right-4 xl:right-6 text-zinc-400 font-medium text-xs sm:text-sm xl:text-lg">
                        {beeperNumber.length}/{pickupMethod === 'nombre' ? 20 : 3}
                      </span>
                    </div>

                    <Button
                      size="icon"
                      onClick={handleContinue}
                      disabled={beeperNumber.trim().length === 0}
                      className="shrink-0 h-12 w-12 sm:h-14 sm:w-14 xl:h-24 xl:w-24 rounded-full bg-emerald-500 hover:bg-emerald-600 text-white shadow-lg disabled:opacity-50 transition-all active:scale-[0.98]"
                    >
                      <ArrowRight className="size-5 sm:size-6 xl:size-10 stroke-[3]" />
                    </Button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Order Fast Logo */}
        <div className="pb-8 flex justify-center pointer-events-none opacity-80 shrink-0 mt-auto">
          <Image
            src="/icons/of-logo.png"
            alt="Order Fast"
            width={160}
            height={60}
            className="object-contain"
            style={{ width: 'auto', height: 'auto' }}
          />
        </div>
      </div>
    </div>
  );
}
